import { storeToRefs } from 'pinia';
import { defineComponent, computed, onMounted, ref, watch, nextTick, } from 'vue';
import { usePayoutAccountsStore } from '@/store/modules/payout-accounts';
import { usePaymentStore } from '@/store/modules/payment';
import useTransactionsStore from '@/store/modules/transactions';
import { useAuthStore } from '@/store/modules/auth';
import { Button, CountrySelectList, WidgetDropDownSingleValue, WidgetInput, } from '@yellowcard/components-library-vue3';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import { PayoutAccountsTypes } from '@/types/payout-accounts';
import { PaymentTypes } from '@/types/payment';
import router, { RoutePaths } from '@/router';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
var PayoutComponents = PayoutAccountsTypes.PayoutComponents;
var PayoutFlow = PayoutAccountsTypes.PayoutFlow;
export default defineComponent({
    name: 'MomoForm',
    components: {
        LoaderComponent,
        Button,
        CountrySelectList,
        WidgetDropDownSingleValue,
        WidgetInput,
    },
    setup() {
        const payoutAccountsStore = usePayoutAccountsStore();
        const { activePayoutAccount, currentStep, countryConfigOptions, defaultCountry, error, isUpdateFlow, loading, networks, paymentNetworks, rules, selectedPaymentNetwork, selectedPayoutMethod, fromTransaction, payoutFlow, payoutAccounts, } = storeToRefs(payoutAccountsStore);
        const paymentStore = usePaymentStore();
        const { sellPaymentAccountName, userMomoAccountNumber, currentStep: paymentCurrentStep, } = storeToRefs(paymentStore);
        const transactionsStore = useTransactionsStore();
        const { channel } = storeToRefs(transactionsStore);
        const authStore = useAuthStore();
        const { partnerId } = storeToRefs(authStore);
        const isButtonDisabled = ref(true);
        const validateInputs = (newInput) => !(rules.value.mobileNumber(newInput.accountNumber) === true &&
            rules.value.accountName(newInput.accountName) === true);
        onMounted(async () => {
            isButtonDisabled.value = validateInputs({
                accountName: activePayoutAccount.value.accountName,
                accountNumber: activePayoutAccount.value.accountNumber,
            });
            if (!fromTransaction.value) {
                const channels = await transactionsStore.getWithdrawChannelsWithoutFees(WidgetApiServiceTypes.ChannelTypes.MOMO);
                if (channels.length) {
                    channel.value = { id: channels[0].id };
                }
            }
            await payoutAccountsStore.getNetworks();
        });
        const loadingSelectOptions = computed(() => {
            if (paymentNetworks.value?.length)
                return '';
            return loading.value;
        });
        const savePayoutAccount = async () => {
            trackEvent(MIXPANEL_EVENTS.SELL.PAYOUT_DETAILS, {
                partner: partnerId.value,
                payment_method: selectedPayoutMethod.value,
            });
            if (!fromTransaction.value)
                return;
            // if coming from transaction flow
            const accounts = payoutAccounts.value.filter(account => account.accountType === selectedPayoutMethod.value);
            if (payoutFlow.value === PayoutFlow.Create && accounts.length) {
                // when "use new payout account" option is clicked
                await payoutAccountsStore.addPayoutAccount();
                selectedPaymentNetwork.value = {
                    id: activePayoutAccount.value.networkId,
                };
                await transactionsStore.getWalletDetails();
                return;
            }
            // if no saved payout account exists for momo
            paymentCurrentStep.value =
                PaymentTypes.SellPaymentSteps.SAVE_PAYOUT_ACCOUNT;
            await router.push(RoutePaths.Payment);
        };
        const saveButtonsCTA = computed(() => [
            {
                disabled: isButtonDisabled.value || !!loading.value,
                text: loading.value || 'Save',
                onClick: async (event) => {
                    event.preventDefault();
                    await payoutAccountsStore.addPayoutAccount();
                    currentStep.value = PayoutComponents.ManagePayouts;
                    payoutAccountsStore.resetActivePayoutAccount();
                },
                show: !fromTransaction.value &&
                    currentStep.value === PayoutComponents.FormSelect,
            },
            {
                disabled: isButtonDisabled.value || !!loading.value,
                text: loading.value || 'Continue',
                onClick: savePayoutAccount,
                show: fromTransaction.value &&
                    currentStep.value === PayoutComponents.FormSelect,
            },
        ]);
        const updateButtonsCTA = computed(() => [
            {
                disabled: isButtonDisabled.value || !!loading.value,
                text: loading.value || 'Update',
                onClick: async () => {
                    await payoutAccountsStore.updatePayoutAccount();
                    currentStep.value = PayoutComponents.ManagePayouts;
                    payoutAccountsStore.resetActivePayoutAccount();
                },
                show: true,
            },
            {
                disabled: !!loading.value,
                color: 'danger',
                variant: 'outline',
                text: 'Delete',
                onClick: () => {
                    currentStep.value = PayoutComponents.DeleteConfirmation;
                },
                show: true,
            },
        ]);
        const buttonsCTA = computed(() => isUpdateFlow.value ? updateButtonsCTA.value : saveButtonsCTA.value);
        const onMOMOSelected = (event) => {
            const selectedNetwork = paymentNetworks.value.find(network => network.code === event) ?? null;
            selectedPaymentNetwork.value = selectedNetwork;
            payoutAccountsStore.setActivePayoutAccount({
                networkId: selectedNetwork?.id,
                networkName: selectedNetwork?.code,
            });
        };
        watch(activePayoutAccount.value, newInput => {
            nextTick(() => {
                isButtonDisabled.value = validateInputs({
                    accountName: newInput.accountName,
                    accountNumber: newInput.accountNumber,
                });
                userMomoAccountNumber.value = newInput.accountNumber;
                sellPaymentAccountName.value = newInput.accountName;
            });
        }, { deep: true });
        return {
            activePayoutAccount,
            buttonsCTA,
            countryConfigOptions,
            defaultCountry,
            error,
            loadingSelectOptions,
            momoNetworks: networks,
            onMOMOSelected,
            rules,
        };
    },
});
